import React from 'react';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import { Description as DescriptionIcon } from '@mui/icons-material';
import { format } from 'date-fns';

function SearchResults({ results, query, onSelectPage }) {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };
  
  // Function to highlight search terms in text
  const highlightText = (text, query) => {
    if (!query || !text) return text;
    
    const parts = String(text).split(new RegExp(`(${query})`, 'gi'));
    
    return parts.map((part, index) => 
      part.toLowerCase() === query.toLowerCase() 
        ? <mark key={index} style={{ backgroundColor: '#fff9c4' }}>{part}</mark> 
        : part
    );
  };
  
  // Function to create a snippet from content
  const createSnippet = (content, query, maxLength = 200) => {
    if (!content) return '';
    
    // Strip HTML tags
    const textContent = content.replace(/<[^>]*>/g, '');
    
    // Find the position of the query in the text
    const position = textContent.toLowerCase().indexOf(query.toLowerCase());
    
    if (position === -1) {
      // If query not found, return the beginning of the content
      return textContent.substring(0, maxLength) + '...';
    }
    
    // Calculate start and end positions for the snippet
    const start = Math.max(0, position - maxLength / 2);
    const end = Math.min(textContent.length, position + query.length + maxLength / 2);
    
    // Create the snippet
    let snippet = '';
    if (start > 0) snippet += '...';
    snippet += textContent.substring(start, end);
    if (end < textContent.length) snippet += '...';
    
    return snippet;
  };
  
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Search Results
      </Typography>
      
      <Typography variant="subtitle1" gutterBottom>
        {results.length} {results.length === 1 ? 'result' : 'results'} for "{query}"
      </Typography>
      
      {results.length === 0 ? (
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography>No results found. Try a different search term.</Typography>
        </Paper>
      ) : (
        <List sx={{ mt: 2 }}>
          {results.map((result, index) => (
            <React.Fragment key={result._id}>
              <Paper sx={{ mb: 2 }}>
                <ListItem 
                  button 
                  onClick={() => onSelectPage(result)}
                  sx={{ display: 'block' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <ListItemIcon>
                      <DescriptionIcon />
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        <Typography variant="h6">
                          {highlightText(result.title, query)}
                        </Typography>
                      } 
                    />
                  </Box>
                  
                  <Box sx={{ pl: 7, pr: 2 }}>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {highlightText(createSnippet(result.content, query), query)}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Category: {result.category?.name}
                      </Typography>
                      
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(result.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </ListItem>
              </Paper>
              
              {index < results.length - 1 && <Divider sx={{ mb: 2 }} />}
            </React.Fragment>
          ))}
        </List>
      )}
    </Box>
  );
}

export default SearchResults; 